import React, { useState, useEffect, useRef } from 'react';
import './SimpleRichTextEditor.css';

function SimpleRichTextEditor({ 
    label, 
    fieldName, 
    value, 
    onChange, 
    placeholder = "Enter your text...",
    height = "120px",
    fontFamily = null,
    fontSize = null,
    fontWeight = null,
    textColor = null
}) {
    const [editorValue, setEditorValue] = useState(value || '');
    const [isEditing, setIsEditing] = useState(false);
    const editorRef = useRef(null);
    const cursorPositionRef = useRef(null);

    // ✅ CURSOR POSITION SAVER
    const saveCursorPosition = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0 && editorRef.current && editorRef.current.contains(selection.anchorNode)) {
            const range = selection.getRangeAt(0);
            cursorPositionRef.current = {
                startOffset: range.startOffset,
                endOffset: range.endOffset,
                startContainer: range.startContainer,
                endContainer: range.endContainer
            };
        }
    };

    // ✅ CURSOR POSITION RESTORER
    const restoreCursorPosition = () => {
        if (cursorPositionRef.current && editorRef.current) {
            try {
                const selection = window.getSelection();
                const range = document.createRange();
                
                // Versuche die gespeicherte Position wiederherzustellen
                if (cursorPositionRef.current.startContainer && 
                    editorRef.current.contains(cursorPositionRef.current.startContainer)) {
                    range.setStart(cursorPositionRef.current.startContainer, cursorPositionRef.current.startOffset);
                    range.setEnd(cursorPositionRef.current.endContainer, cursorPositionRef.current.endOffset);
                } else {
                    // Fallback: Cursor ans Ende
                    range.selectNodeContents(editorRef.current);
                    range.collapse(false);
                }
                
                selection.removeAllRanges();
                selection.addRange(range);
            } catch (error) {
                // Fallback: Cursor ans Ende
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(editorRef.current);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    };

    useEffect(() => {
        // ✅ INITIALER CONTENT SETUP - Nur beim ersten Laden oder wenn nicht editiert wird
        if (editorRef.current && !isEditing) {
            const currentContent = editorRef.current.innerHTML;
            const newValue = value || '';
            
            // Nur aktualisieren wenn sich der Wert wirklich geändert hat
            if (currentContent !== newValue && newValue !== editorValue) {
                editorRef.current.innerHTML = newValue;
                setEditorValue(newValue);
            }
        }
    }, [value, isEditing]);

    // ✅ INITIAL CONTENT SETUP
    useEffect(() => {
        if (editorRef.current && editorValue && !editorRef.current.innerHTML) {
            editorRef.current.innerHTML = editorValue;
        }
    }, [editorValue]);

    const handleChange = (content) => {
        setEditorValue(content);
        
        if (!fieldName) {
            console.error('⚠️ SimpleRichTextEditor: fieldName is required but undefined!');
            return;
        }
        
        const fakeEvent = {
            target: {
                name: fieldName,
                value: content, // ✅ Speichere HTML für Bold-Funktionalität
                type: 'text'
            }
        };
        
        if (onChange) {
            onChange(fakeEvent);
        }
    };

    // ✅ BOLD TOGGLE FUNCTION
    const toggleBold = () => {
        const editor = editorRef.current;
        if (!editor) return;

        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        
        if (range.collapsed) {
            // Kein Text ausgewählt - füge Bold-Tags am Cursor ein
            const boldText = document.createElement('strong');
            boldText.textContent = 'Bold text';
            range.insertNode(boldText);
            
            // Cursor nach dem Bold-Text setzen
            range.setStartAfter(boldText);
            range.setEndAfter(boldText);
            selection.removeAllRanges();
            selection.addRange(range);
        } else {
            // Text ausgewählt - Bold toggle
            const selectedText = range.toString();
            const parentElement = range.commonAncestorContainer.parentElement;
            
            if (parentElement && parentElement.tagName === 'STRONG') {
                // Text ist bereits bold - entferne Bold
                const textNode = document.createTextNode(selectedText);
                parentElement.parentNode.replaceChild(textNode, parentElement);
            } else {
                // Text ist nicht bold - füge Bold hinzu
                const boldElement = document.createElement('strong');
                boldElement.textContent = selectedText;
                range.deleteContents();
                range.insertNode(boldElement);
            }
        }
        
        // Update content
        handleChange(editor.innerHTML);
        editor.focus();
    };

    // ✅ PASTE HANDLER - Nur Plain Text erlauben
    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
        handleChange(editorRef.current.innerHTML);
    };

    // ✅ INPUT HANDLER mit Cursor-Position Schutz
    const handleInput = () => {
        if (editorRef.current) {
            saveCursorPosition(); // Speichere Position vor Update
            handleChange(editorRef.current.innerHTML);
        }
    };

    // ✅ KEYBOARD SHORTCUTS
    const handleKeyDown = (e) => {
        // Ctrl+B oder Cmd+B für Bold
        if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
            e.preventDefault();
            toggleBold();
        }
        
        // ✅ ENTER HANDLER: Normale Zeilenumbrüche
        if (e.key === 'Enter') {
            e.preventDefault();
            document.execCommand('insertHTML', false, '<br><br>');
            handleChange(editorRef.current.innerHTML);
        }
    };

    // ✅ DYNAMISCHE EDITOR STYLES
    const editorStyle = {
        minHeight: height,
        fontFamily: fontFamily || 'inherit',
        fontSize: fontSize || '14px',
        fontWeight: fontWeight || '400',
        color: textColor || '#333333',
        // ✅ WHITE-SPACE KONTROLLE für Editor
        whiteSpace: 'pre-wrap', // ✅ Respektiert Leerzeichen und \n
        wordWrap: 'break-word',
        overflowWrap: 'break-word'
    };

    return (
        <div className="simple-rich-text-editor-container">
            {label && <label className="simple-rich-text-editor-label">{label}</label>}
            
            {/* Toolbar */}
            <div className="simple-rich-text-toolbar">
                <button
                    type="button"
                    className="toolbar-button"
                    onClick={toggleBold}
                    title="Bold (Ctrl+B)"
                >
                    <strong>B</strong>
                </button>
                <div className="toolbar-separator"></div>
                <span className="toolbar-hint">Ctrl+B für Bold • Enter für Zeilenumbruch</span>
            </div>
            
            {/* Editor */}
            <div 
                ref={editorRef}
                className="simple-rich-text-editor"
                contentEditable
                suppressContentEditableWarning={true}
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsEditing(true)}
                onBlur={() => setIsEditing(false)}
                style={editorStyle}
                data-placeholder={placeholder}
            />
        </div>
    );
}

export default SimpleRichTextEditor;
